import moment from "moment-timezone";
import { capitalize, cloneDeep, each } from "lodash";

import { DEFAULT_ROUTE } from "../consts/routeDetails";
import { DEFAULT_TIMEBAND, INVALID_TIME_FORMAT } from "../consts/strings";
import { SAME_DAY, PLANNED } from "../consts/collections";
import { FINAL_ROUTES_TIME } from "../consts/timeSlider";
import { OUTER, SECTOR, POSTCODE } from "../consts/layers";
import { DEFAULT_LABEL_CHANGES, ROUTE_STATUS } from "../consts/routeDetails";
import {
  LEVEL_TO_POSTCODE_KEY,
  ADD,
  REPLACE,
  REMOVE,
  ADD_REDIRECT,
  REMOVE_REDIRECT,
} from "../consts/map";
import {
  COLLECTIONS_PLAN_TYPE,
  DEFAULT_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
} from "../consts/planTypes";
import { palette } from "../theme/palette";

import {
  timestampToHHMM,
  timeBandToInteger,
  getTimestampFromHHMM,
  isHHMMFormat,
  getDiffInMinutes,
} from "./date";
import {
  calculateTotals,
  overlayDetailsAsObject,
  resetVolumeOverrideStopType,
  resetVolumePickupStopType,
} from "./volumeData";
import { groupPostcodes, groupSectors } from "./lookUpTables";
import { processRouteStops } from "./volumes";
import { processRoutesTimes } from "./routesTimes";
import Color from "color";
import { Postcode } from "./postcode";
import { getBbox } from "./map";
import { divideToInt } from "./math";

export const formatLongTime = (time) => {
  const times = time ? time.split(":") : ["00", "00"];
  return `${times[0]}:${times[1]}`;
};

export const generateColor = (index) => {
  const saturation = "50%";
  const lightness = "70%";

  const hue = index * 137.508; // use golden angle approximation
  const hueMod360 = hue % 360;
  const colour = `hsl(${hueMod360}, ${saturation}, ${lightness})`;
  return { colour, textColour: getTextColour(colour) };
};

const getTextColour = (colour) => {
  try {
    const newColour = Color(colour);
    return newColour.isDark()
      ? palette.typography.white.high
      : palette.typography.black.high;
  } catch (error) {
    return palette.typography.white.high;
  }
};

export const processRoutes = (routes = [], routeConfig = []) => {
  const configs = {};
  routeConfig.forEach((config) => {
    configs[config.plannedReadyTimeBand] = config.outer;
  });

  return routes.map((route, index) => {
    const colour = generateColor(index);

    const sectors = [];
    const postcodes = [];

    const outer = [];
    const sector = [];
    const postcode = [];

    configs[route.plannedReadyTime ?? "default"]?.forEach((o) => {
      if (o.routeCode === route.routeCode) {
        outer.push({ postcodeOuter: o.outer });
      }
      o.sector && sectors.push(...o.sector);
      o.postcode && postcodes.push(...o.postcode);
    });

    sectors.forEach((s) => {
      if (s.routeCode === route.routeCode) {
        sector.push({ postcodeSector: s.sector });
      }
    });

    postcodes.forEach((p) => {
      if (p.routeCode === route.routeCode) {
        postcode.push({ postcode: p.postcode });
      }
    });

    return {
      ...route,
      plannedReadyTimeAsString: getTimeFromDate(route.plannedReadyTime),
      routeCode: route.routeCode,
      ...colour,
      outer,
      sector,
      postcode,
    };
  });
};

export const applyChangeToRoutes = ({ routes, change }) => {
  routes = [...routes];
  routes = processActionRangeRemovals({
    routes,
    change,
  });

  const routeEditIndex = routes.findIndex(
    ({ routeCode }) => routeCode === change.routeEditCode
  );
  const routeEdit = { ...routes[routeEditIndex] };
  routes[routeEditIndex] = routeEdit;

  if (routeEdit.routeType !== change.planType)
    routeEdit.routeType = change.planType;
  if (
    !routeEdit.plannedReadyTime ||
    !routeHasTerritory(routeEdit, change.planType)
  ) {
    const isFinalWave = change.timeBand === "default";
    routeEdit.plannedReadyTimeAsString = isFinalWave ? null : change.timeBand;
    routeEdit.plannedReadyTime = isFinalWave
      ? null
      : getTimestampFromHHMM(change.timeBand);
  }

  routes = getProcessActionFunction(change)({
    ...change,
    routes,
    postcode: new Postcode(change.postcode),
    routeEdit,
  });

  routes = processActionConsolidation({
    routes,
    change,
  });

  return routes;
};

const processActionRangeRemovals = ({ change, routes }) => {
  (change.rangeRemovals || []).forEach(({ routeCode, sectors }) => {
    const routeIndex = routes.findIndex(
      (route) => route.routeCode === routeCode
    );
    if (routeIndex === -1) return;
    const route = { ...routes[routeIndex] };
    route.sector = route.sector.filter(
      (s) => !sectors.includes(s.postcodeSector)
    );
    route.postcode = route.postcode.filter(
      (p) => !sectors.includes(Postcode.toSector(p.postcode))
    );
    if (
      isNonRedirectionRouteCode(routeCode) ||
      routeHasTerritory(route, change.planType)
    )
      routes.splice(routeIndex, 1, route);
    else routes = routes.filter((route) => route.routeCode !== routeCode);
  });
  return routes;
};

const processActionConsolidation = ({ change, routes }) => {
  const {
    toOuter,
    toSector,
    preventSectorAllocation,
    outerAffectedRouteCode,
    sectorAffectedRouteCode,
  } = change.consolidation || {};

  if (outerAffectedRouteCode) {
    const outerAffectedRouteIndex = routes.findIndex(
      ({ routeCode }) => routeCode === outerAffectedRouteCode
    );
    const outerAffectedRoute = { ...routes[outerAffectedRouteIndex] };
    outerAffectedRoute.outer = outerAffectedRoute.outer.filter(
      (o) => o.postcodeOuter !== change.outer
    );
    routes.splice(outerAffectedRouteIndex, 1, outerAffectedRoute);
  }

  if (sectorAffectedRouteCode) {
    const sectorAffectedRouteIndex = routes.findIndex(
      ({ routeCode }) => routeCode === sectorAffectedRouteCode
    );
    const sectorAffectedRoute = { ...routes[sectorAffectedRouteIndex] };
    sectorAffectedRoute.sector = sectorAffectedRoute.sector.filter(
      (s) => s.postcodeSector !== change.sector
    );
    if (
      !isRedirectionRouteCode(sectorAffectedRouteCode) ||
      routeHasTerritory(sectorAffectedRoute, change.planType)
    )
      routes.splice(sectorAffectedRouteIndex, 1, sectorAffectedRoute);
    else
      routes = routes.filter(
        ({ routeCode }) => routeCode !== sectorAffectedRouteCode
      );
  }

  const consolidationRouteIndex = routes.findIndex(
    ({ routeCode }) => routeCode === change.routeCode
  );
  const consolidationRoute = { ...routes[consolidationRouteIndex] };
  if (toOuter) {
    consolidationRoute.sector = consolidationRoute.sector.filter(
      (s) => Postcode.toOuter(s.postcodeSector) !== change.outer
    );
    consolidationRoute.postcode = consolidationRoute.postcode.filter(
      (p) => Postcode.toOuter(p.postcode) !== change.outer
    );
  } else if (toSector) {
    consolidationRoute.postcode = consolidationRoute.postcode.filter(
      (p) => Postcode.toSector(p.postcode) !== change.sector
    );
  }

  if (change.type === ADD && toOuter)
    consolidationRoute.outer = [
      ...consolidationRoute.outer,
      { postcodeOuter: change.outer },
    ];
  else if (!toOuter && !preventSectorAllocation && toSector)
    consolidationRoute.sector = [
      ...consolidationRoute.sector,
      { postcodeSector: change.sector },
    ];

  if (
    isNonRedirectionRouteCode(change.routeCode) ||
    routeHasTerritory(consolidationRoute, change.planType)
  )
    routes.splice(consolidationRouteIndex, 1, consolidationRoute);
  else
    routes = routes.filter(({ routeCode }) => routeCode !== change.routeCode);

  return routes;
};

const getProcessActionFunction = ({ type }) => {
  switch (type) {
    case ADD:
      return processAddAction;
    case REPLACE:
      return processReplaceAction;
    case REMOVE:
      return processRemoveAction;
    case ADD_REDIRECT:
      return processAddRedirectAction;
    case REMOVE_REDIRECT:
      return processRemoveRedirectAction;
    default:
      throw new Error("Unknown edit option type");
  }
};

const processAddAction = ({
  postcode,
  planType,
  routes,
  affectedRouteCode,
  routeEditCode,
}) => {
  const level = postcode.getLevel();
  const routeEditIndex = routes.findIndex(
    ({ routeCode }) => routeCode === routeEditCode
  );
  const routeEdit = { ...routes[routeEditIndex] };
  routeEdit[level.toLowerCase()] = [
    ...routeEdit[level.toLowerCase()],
    { [LEVEL_TO_POSTCODE_KEY[level]]: postcode.getPostcode(level) },
  ];
  routes[routeEditIndex] = routeEdit;

  const affectedRoute = affectedRouteCode && {
    ...routes.find(({ routeCode }) => routeCode === affectedRouteCode),
  };
  if (
    affectedRoute &&
    affectedRoute.plannedReadyTime === routeEdit.plannedReadyTime
  ) {
    affectedRoute[level.toLowerCase()] = affectedRoute[
      level.toLowerCase()
    ].filter(
      (item) =>
        item[LEVEL_TO_POSTCODE_KEY[level]] !== postcode.getPostcode(level)
    );

    if (
      isNonRedirectionRouteCode(affectedRouteCode) ||
      routeHasTerritory(affectedRoute, planType)
    )
      routes.splice(
        routes.findIndex((item) => item.routeCode === affectedRouteCode),
        1,
        affectedRoute
      );
    else
      routes = routes.filter(
        ({ routeCode }) => routeCode !== affectedRouteCode
      );
  }
  return routes;
};

const processReplaceAction = ({
  postcode,
  routeEditCode,
  affectedRouteCode,
  routes,
}) => {
  const level = postcode.getLevel();
  const routeEditIndex = routes.findIndex(
    ({ routeCode }) => routeCode === routeEditCode
  );
  const routeEdit = { ...routes[routeEditIndex] };
  routeEdit[level.toLowerCase()] = routeEdit[level.toLowerCase()].filter(
    ({ [LEVEL_TO_POSTCODE_KEY[level]]: postcodeKey }) =>
      postcodeKey !== postcode.getPostcode(level)
  );
  routes[routeEditIndex] = routeEdit;

  const affectedRouteIndex = routes.findIndex(
    ({ routeCode }) => routeCode === affectedRouteCode
  );
  const affectedRoute = { ...routes[affectedRouteIndex] };
  affectedRoute[level.toLowerCase()] = [
    ...affectedRoute[level.toLowerCase()],
    { [LEVEL_TO_POSTCODE_KEY[level]]: postcode.getPostcode(level) },
  ];
  routes[affectedRouteIndex] = affectedRoute;

  return routes;
};

const processRemoveAction = ({ affectedRouteCode, postcode, routes }) => {
  const level = postcode.getLevel();
  const affectedRouteIndex = routes.findIndex(
    ({ routeCode }) => routeCode === affectedRouteCode
  );
  const affectedRoute = { ...routes[affectedRouteIndex] };
  affectedRoute[level.toLowerCase()] = affectedRoute[
    level.toLowerCase()
  ].filter(
    ({ [LEVEL_TO_POSTCODE_KEY[level]]: postcodeKey }) =>
      postcodeKey !== postcode.getPostcode(level)
  );
  routes[affectedRouteIndex] = affectedRoute;
  return routes;
};

const processAddRedirectAction = ({
  affectedRouteCode,
  postcode,
  timeBand,
  planType,
  routes,
}) => {
  const level = postcode.getLevel();

  const redirectionRouteCode = getRedirectionRouteCode(planType, timeBand);
  const redirectionIndex = routes.findIndex(
    ({ routeCode }) => routeCode === redirectionRouteCode
  );
  const redirectionRoute =
    redirectionIndex === -1
      ? getNewRedirectionRoute(planType, timeBand)
      : { ...routes[redirectionIndex] };

  redirectionRoute[level.toLowerCase()] = [
    ...redirectionRoute[level.toLowerCase()],
    { [LEVEL_TO_POSTCODE_KEY[level]]: postcode.getPostcode(level) },
  ];

  const redirectHasTerritory = routeHasTerritory(redirectionRoute, planType);

  if (redirectionIndex !== -1 && redirectHasTerritory)
    routes[redirectionIndex] = redirectionRoute;
  else if (redirectHasTerritory) routes.push(redirectionRoute);
  else
    routes = routes.filter(
      ({ routeCode }) => routeCode !== redirectionRouteCode
    );

  const affectedRouteIndex = routes.findIndex(
    ({ routeCode }) => routeCode === affectedRouteCode
  );
  if (affectedRouteIndex !== -1) {
    const affectedRoute = { ...routes[affectedRouteIndex] };
    affectedRoute[level.toLowerCase()] = affectedRoute[
      level.toLowerCase()
    ].filter(
      ({ [LEVEL_TO_POSTCODE_KEY[level]]: key }) =>
        key !== postcode.getPostcode(level)
    );
    routes[affectedRouteIndex] = affectedRoute;
  }

  return routes;
};

const processRemoveRedirectAction = ({
  affectedRouteCode,
  planType,
  postcode,
  routes,
}) => {
  const level = postcode.getLevel();

  const affectedRouteIndex = routes.findIndex(
    ({ routeCode }) => routeCode === affectedRouteCode
  );
  const affectedRoute = { ...routes[affectedRouteIndex] };
  affectedRoute[level.toLowerCase()] = affectedRoute[
    level.toLowerCase()
  ].filter(
    ({ [LEVEL_TO_POSTCODE_KEY[level]]: postcodeKey }) =>
      postcodeKey !== postcode.getPostcode(level)
  );

  if (routeHasTerritory(affectedRoute, planType))
    routes[affectedRouteIndex] = affectedRoute;
  else
    routes = routes.filter(({ routeCode }) => routeCode !== affectedRouteCode);

  return routes;
};

export const getRoutesForTimeband = (
  routes,
  selectedTimeBand,
  planType = DEFAULT_PLAN_TYPE,
  collectionType = PLANNED
) => {
  const routesInTimeband = {
    routeOuter: {},
    routeSector: {},
    routePostcode: {},
  };

  if (!!!selectedTimeBand) return routesInTimeband;
  selectedTimeBand = selectedTimeBand
    .replace("23:", "-23:")
    .replace("default", FINAL_ROUTES_TIME);

  const isRouteAfterTime = (route) =>
    routeHasTerritory(route, planType, collectionType) &&
    (!route.plannedReadyTime ||
      route.plannedReadyTimeAsString >= selectedTimeBand);

  const routesAfterTime = routes
    .filter(
      (route) => planType === COLLECTIONS_PLAN_TYPE || isRouteAfterTime(route)
    )
    .sort((a, b) => {
      // sort desc by ready time
      if (!b.plannedReadyTime) return 1;
      if (!a.plannedReadyTime) return -1;
      return a.plannedReadyTimeAsString < b.plannedReadyTimeAsString ? 1 : -1;
    });

  const hasRouteForArea = (postcodeKey, readyTime, levelRoutes) =>
    readyTime >= levelRoutes[postcodeKey]?.plannedReadyTime;

  const inheritsFromAbove = (postcodeKey, readyTime, level) => {
    if (level === OUTER) return false;

    const inheritedRoute =
      (level === POSTCODE &&
        routesInTimeband.routeSector[Postcode.toSector(postcodeKey)]) ||
      routesInTimeband.routeOuter[Postcode.toOuter(postcodeKey)];

    return inheritedRoute && readyTime > inheritedRoute.plannedReadyTime;
  };

  const processRouteLevel = ({ route, level }) => {
    const levelKey = level.toLowerCase();
    const readyTime = getReadyTime(route, planType, collectionType);
    route[levelKey].forEach(
      ({
        [LEVEL_TO_POSTCODE_KEY[level]]: postcodeKey,
        startTime,
        collectionType: areaCollectionType,
      }) => {
        const levelRoutes = routesInTimeband[`route${capitalize(levelKey)}`];

        if (
          (planType === COLLECTIONS_PLAN_TYPE &&
            areaCollectionType !== collectionType) ||
          hasRouteForArea(postcodeKey, readyTime, levelRoutes) ||
          inheritsFromAbove(postcodeKey, readyTime, level)
        )
          return;

        levelRoutes[postcodeKey] = {
          routeCode: route.routeCode,
          plannedReadyTime: readyTime,
          [levelKey]: postcodeKey,
          startTime,
        };
      }
    );
  };

  [OUTER, SECTOR, POSTCODE].forEach((level) =>
    routesAfterTime.forEach((route) => processRouteLevel({ route, level }))
  );
  return routesInTimeband;
};

export const getBoundingBoxForRoute = ({
  route,
  type = POSTCODE,
  routePostcodes = DEFAULT_LABEL_CHANGES,
  lookupTables = {},
}) => {
  if (!route) {
    return;
  }

  const items = routePostcodes[type][route];
  if (!items) {
    return;
  }

  const lookupTable = lookupTables[type];
  if (!lookupTable) return [];
  return getBbox({ postcodes: items, lookupTable });
};

export const getTimeFromDate = (date) => {
  if (!date) return;
  return moment(date).format("HH:mm");
};

export const removeVolumeOverlayFromRoutes = (routes = []) => {
  return routes.map((route) => {
    delete route.routeOptimisation;
    delete route.stops;
    route.outer = route.outer.map((outer) => {
      delete outer.stopsCount;
      return outer;
    });
    route.sector = route.sector.map((sector) => {
      delete sector.stopsCount;
      return sector;
    });
    route.postcode = route.postcode.map((postcode) => {
      delete postcode.stopsCount;
      return postcode;
    });
    return route;
  });
};

const processTimesAndVolumes = async ({
  routeConfig,
  redirectConfig,
  routes,
  volumeData,
  overrideAreas,
  pickupAreas,
  locationConfig,
  lookupTables,
  planType,
}) => {
  routes = cloneDeep(routes);
  resetRouteVolumes({ routes });
  resetRouteTimes({ routes });

  const { routesTimes, routesByOuter, cutOffTimes } = processRoutesTimes({
    routeConfig,
    routes,
    groupedPostcodes: groupPostcodes(lookupTables.POSTCODE),
    groupedSectors: groupSectors(lookupTables.SECTOR),
    planType,
  });

  const routesWithStartTimes = margeTimesIntoRoutes({
    routes,
    routesTimes,
  });

  if (!volumeData) {
    return {
      routes: routesWithStartTimes,
      routesByOuter,
      routesTimes,
      cutOffTimes,
    };
  }

  if (planType === OVERRIDES_PLAN_TYPE) {
    volumeData = cloneDeep(volumeData);
    resetVolumeOverrideStopType({ volumeData });
  }
  if (planType === PICKUP_PLAN_TYPE) {
    volumeData = cloneDeep(volumeData);
    resetVolumePickupStopType({ volumeData });
  }

  const { overlayDetails, routes: routesWithStops } = await processRouteStops({
    routes: routesWithStartTimes,
    redirectConfig,
    overlayDetails: volumeData.overlayDetails,
    overrideAreas,
    pickupAreas,
    locationConfig,
    planType,
  });
  const routesWithStopsMapped = routesWithStops.reduce((prev, curr) => {
    prev[curr.routeCode] = curr;
    return prev;
  }, {});

  return {
    overlayDetails,
    routes: routes.map((route) =>
      Object.keys(routesWithStopsMapped).includes(route.routeCode)
        ? routesWithStopsMapped[route.routeCode]
        : route
    ),
    routesByOuter,
    routesTimes,
    cutOffTimes,
  };
};

export const applyChangeToTimesAndVolumes = async ({
  routeConfig,
  redirectConfig,
  routes,
  routesByOuter,
  routesTimes,
  cutOffTimes,
  volumeData,
  overrideAreas,
  pickupAreas,
  locationConfig,
  lookupTables,
  change,
}) => {
  routes = routes.map((route) =>
    change.impactRouteCodes.includes(route.routeCode)
      ? resetRouteOuter({
          route,
          outer: change.outer,
        })
      : route
  );

  ({ routesTimes, routesByOuter, cutOffTimes } = processRoutesTimes({
    routeConfig,
    routes,
    groupedPostcodes: groupPostcodes(lookupTables.POSTCODE),
    groupedSectors: groupSectors(lookupTables.SECTOR),
    routesTimes,
    routesByOuter,
    cutOffTimes,
    planType: change.planType,
    change,
  }));

  routes = margeTimesIntoRoutes({
    routes,
    routesTimes,
    change,
  });

  let routesWithStops;
  let overlayDetails;
  if (volumeData) {
    ({ overlayDetails, routes: routesWithStops } = await processRouteStops({
      routes,
      redirectConfig,
      affectedOuter: change.outer,
      affectedRoutes: routes.filter(({ routeCode }) =>
        change.impactRouteCodes.includes(routeCode)
      ),
      overlayDetails: volumeData.overlayDetails,
      overrideAreas,
      pickupAreas,
      locationConfig,
      planType: change.planType,
    }));
    const routesWithStopsMapped = routesWithStops.reduce((acc, route) => {
      acc[route.routeCode] = route;
      return acc;
    }, {});
    routes = routes.map((route) =>
      Object.keys(routesWithStopsMapped).includes(route.routeCode)
        ? routesWithStopsMapped[route.routeCode]
        : route
    );
  }

  return {
    overlayDetails,
    routes,
    routesTimes,
    routesByOuter,
    cutOffTimes,
  };
};

const margeTimesIntoRoutes = ({ routes, routesTimes, change }) => {
  return !change
    ? routes.map((route) => {
        if (!routesTimes[route.routeCode]) return route;
        route.outer = route.outer.map((outer) => ({
          ...outer,
          ...routesTimes[route.routeCode]?.outer?.[outer.postcodeOuter],
        }));
        route.sector = route.sector.map((sector) => ({
          ...sector,
          ...routesTimes[route.routeCode]?.sector?.[sector.postcodeSector],
        }));
        route.postcode = route.postcode.map((postcode) => ({
          ...postcode,
          ...routesTimes[route.routeCode]?.postcode?.[postcode.postcode],
        }));
        return route;
      })
    : routes.map((route) => {
        const { routeCode } = route;
        if (
          !change.impactRouteCodes.includes(routeCode) ||
          !routesTimes[routeCode]
        )
          return route;
        route.outer = route.outer.map((o) => {
          if (o.postcodeOuter !== change.outer) return o;
          else
            return {
              ...o,
              ...routesTimes[routeCode]?.outer?.[o.postcodeOuter],
            };
        });
        route.sector = route.sector.map((s) => {
          if (Postcode.toOuter(s.postcodeSector) !== change.outer) return s;
          else
            return {
              ...s,
              ...routesTimes[routeCode]?.sector?.[s.postcodeSector],
            };
        });
        route.postcode = route.postcode.map((p) => {
          if (Postcode.toOuter(p.postcode) !== change.outer) return p;
          else
            return {
              ...p,
              ...routesTimes[routeCode]?.postcode?.[p.postcode],
            };
        });
        return route;
      });
};

const resetRouteTimes = ({ routes }) => {
  routes.forEach((route) => {
    route.outer.forEach((outer) => {
      outer.startTime = null;
      outer.redirectStartTime = null;
    });
    route.sector.forEach((sector) => {
      sector.startTime = null;
      sector.redirectStartTime = null;
    });
    route.postcode.forEach((postcode) => {
      postcode.startTime = null;
      postcode.redirectStartTime = null;
    });
  });
};

const resetRouteVolumes = ({ routes }) => {
  routes.forEach((route) => {
    route.stops = [];
    route.outer.forEach((outer) => {
      outer.stopsCount = 0;
    });
    route.sector.forEach((sector) => {
      sector.stopsCount = 0;
    });
    route.postcode.forEach((postcode) => {
      postcode.stopsCount = 0;
    });
  });
};

const resetRouteOuter = ({ route, outer }) => {
  route = cloneDeep(route);
  route.stops = route.stops.filter(
    ({ postcode }) => Postcode.toOuter(postcode) !== outer
  );
  route.outer.forEach((o) => {
    if (o.postcodeOuter !== outer) return;
    o.startTime = null;
    o.redirectStartTime = null;
    o.stopsCount = 0;
  });
  route.sector.forEach((s) => {
    if (Postcode.toOuter(s.postcodeSector) !== outer) return;
    s.startTime = null;
    s.redirectStartTime = null;
    s.stopsCount = 0;
  });
  route.postcode.forEach((p) => {
    if (Postcode.toOuter(p.postcode) !== outer) return;
    p.startTime = null;
    p.redirectStartTime = null;
    p.stopsCount = 0;
  });
  return route;
};

export const getReadyTime = (route, planType, collectionType) => {
  if (!route) return null;
  return (
    route.plannedReadyTimeAsString ||
    (routeHasTerritory(route, planType, collectionType) &&
      !route.plannedReadyTime &&
      FINAL_ROUTES_TIME)
  );
};

export const routeHasTerritory = (
  route,
  planType,
  collectionType = PLANNED
) => {
  if (planType === PICKUP_PLAN_TYPE) return !!route?.pickupAllocated;
  else if (planType === OVERRIDES_PLAN_TYPE) return !!route?.overridesAllocated;
  else if (planType === LOCATIONS_PLAN_TYPE)
    return !!route?.locationCodes?.length;
  else if (planType !== COLLECTIONS_PLAN_TYPE)
    return !!(
      route?.outer?.length ||
      route?.sector?.length ||
      route?.postcode?.length
    );
  else if (collectionType === SAME_DAY) return !!route?.sameDayRoute;
  else return !!route?.plannedRoute;
};

export const isFutureRoute = ({
  route,
  planType,
  collectionType,
  selectedTimeBand,
}) => {
  if (!route || !selectedTimeBand) return false;

  if (!route.plannedReadyTimeAsString) {
    if (routeHasTerritory(route, planType, collectionType)) {
      return selectedTimeBand !== DEFAULT_TIMEBAND;
    }
  } else {
    return (
      selectedTimeBand !== DEFAULT_TIMEBAND &&
      route.plannedReadyTimeAsString.replace("23:", "-23:") >
        selectedTimeBand.replace("23:", "-23:")
    );
  }
};

export const padZeros = (value, size) => {
  let valueString = value + "";
  let padSize = size - valueString.length;
  padSize = padSize > 0 ? padSize : 0;
  return `${new Array(padSize).fill("0").join("")}${valueString}`;
};

export const summaryForRoutes = (routes, drivers, planType, collectionType) => {
  const totalsTemplate = {
    totalStops: 0,
    totalCollections: 0,
    totalParcels: 0,
    totalRoutesAllocated: 0,
    totalExceptions: 0,
    routesWithExceptions: 0,
    totalOpportunity: 0,
    totalDuty: 0,
    totalDutyTime: 0,
  };
  const wavesTotals = {};
  const {
    totalStops,
    totalCollections,
    totalParcels,
    totalRoutesAllocated,
    totalExceptions,
    routesWithExceptions,
    totalOpportunity,
    totalDuty,
    totalDutyTime,
  } = routes.reduce(
    (acc, route) => {
      if (
        !routeHasTerritory(route, planType, collectionType) ||
        isRedirectionRouteCode(route.routeCode)
      )
        return acc;

      const driver = drivers[route.driverCode] || {};
      const waveName = timestampToHHMM(route.plannedReadyTime) || "final";
      if (!wavesTotals[waveName])
        wavesTotals[waveName] = {
          waveName,
          totalRoutes: 0,
          ...totalsTemplate,
        };

      const waveTotals = wavesTotals[waveName];

      const routeStops = route.stops?.length || 0;
      const routeParcels = getParcels(route);
      const routeCollections = route.scheduledCollections?.length || 0;

      waveTotals.totalRoutes += 1;
      acc.totalStops += routeStops;
      waveTotals.totalStops += routeStops;
      acc.totalParcels += routeParcels;
      waveTotals.totalParcels += routeParcels;
      acc.totalCollections += routeCollections;
      waveTotals.totalCollections += routeCollections;
      acc.totalRoutesAllocated += 1;
      waveTotals.totalRoutesAllocated += 1;

      const dutyTime = getDiffInMinutes(
        driver.dutyStartTime,
        driver.dutyEndTime
      );
      acc.totalDutyTime += dutyTime;
      waveTotals.totalDutyTime += dutyTime;
      if (!route.routeOptimisation) return acc;
      const routeExceptionsCount =
        (route.routeOptimisation.serviceExceptions || 0) +
        (route.routeOptimisation.dutyExceptions || 0);

      if (routeExceptionsCount > 0) {
        acc.routesWithExceptions += 1;
        waveTotals.routesWithExceptions += 1;
        acc.totalExceptions += routeExceptionsCount;
        waveTotals.totalExceptions += routeExceptionsCount;
      }

      if (route.routeOptimisation.routeStatus === ROUTE_STATUS.OPPORTUNITY) {
        acc.totalOpportunity += route.routeOptimisation.dutyDifference;
        waveTotals.totalOpportunity += route.routeOptimisation.dutyDifference;
      } else if (
        route.routeOptimisation.routeStatus === ROUTE_STATUS.DUTY_EXCEEDED
      ) {
        acc.totalDuty += Math.abs(route.routeOptimisation.dutyDifference);
        waveTotals.totalDuty += Math.abs(
          route.routeOptimisation.dutyDifference
        );
      }

      return acc;
    },
    { ...totalsTemplate }
  );

  const totalAverageOpportunityMinutes = divideToInt(
    totalOpportunity,
    totalRoutesAllocated
  );
  const totalAverageDutyMinutes = divideToInt(totalDuty, totalRoutesAllocated);
  const totalAverageDutyTime = divideToInt(totalDutyTime, totalRoutesAllocated);
  const totalSPR = divideToInt(totalStops, totalRoutesAllocated);

  Object.values(wavesTotals).forEach((waveTotals) => {
    waveTotals.totalAverageOpportunityMinutes = divideToInt(
      waveTotals.totalOpportunity,
      waveTotals.totalRoutes
    );
    waveTotals.totalAverageDutyMinutes = divideToInt(
      waveTotals.totalDuty,
      waveTotals.totalRoutes
    );
    waveTotals.totalAverageDutyTime = divideToInt(
      waveTotals.totalDutyTime,
      waveTotals.totalRoutes
    );
    waveTotals.totalSPR = divideToInt(
      waveTotals.totalStops,
      waveTotals.totalRoutesAllocated
    );
    waveTotals.totalRoutesPercentage = divideToInt(
      waveTotals.totalRoutes * 100,
      totalRoutesAllocated
    );
    waveTotals.totalStopsPercentage = divideToInt(
      waveTotals.totalStops * 100,
      totalStops
    );
    waveTotals.totalParcelsPercentage = divideToInt(
      waveTotals.totalParcels * 100,
      totalParcels
    );
    waveTotals.totalCollectionsPercentage = divideToInt(
      waveTotals.totalCollections * 100,
      totalCollections
    );
    waveTotals.totalExceptionsPercentage = divideToInt(
      waveTotals.totalExceptions * 100,
      totalExceptions
    );
  });

  return {
    totalStops,
    totalCollections,
    totalParcels,
    totalRoutesAllocated,
    routesWithExceptions,
    totalExceptions,
    totalAverageOpportunityMinutes,
    totalAverageDutyMinutes,
    totalAverageDutyTime,
    totalSPR,
    waves: Object.values(wavesTotals).sort(({ waveName: a }, { waveName: b }) =>
      a.localeCompare(b)
    ),
  };
};

export const getRouteHasVolume = ({
  route,
  postcode,
  zoomLevel,
  selectedTimeBand,
}) => {
  const currentPostcode = new Postcode(postcode);
  return !!route.stops?.find((stop) => {
    const stopPostcode = new Postcode(stop.postcode);
    return (
      stopPostcode
        .toPostcode()
        .includes(currentPostcode.getPostcode(zoomLevel)) &&
      timeBandToInteger(stop.timeBand) <= timeBandToInteger(selectedTimeBand)
    );
  });
};

export const getRouteVolume = ({
  routeCode,
  route,
  postcode,
  volumes,
  zoomLevel,
  showAll,
  individual,
  selectedRoute,
  selectedTimeBand,
}) => {
  const currentPostcode = new Postcode(postcode);
  if (!!!route?.stops) {
    return {
      stops: volumes?.[currentPostcode.getPostcode(zoomLevel)]?.stops || 0,
      parcels: volumes?.[currentPostcode.getPostcode(zoomLevel)]?.parcels || 0,
    };
  }

  let stops = null;
  if (showAll) {
    stops = route.stops;
  } else if (
    !individual &&
    (!selectedRoute || selectedRoute.routeCode !== routeCode || !postcode)
  ) {
    stops = route.stops.filter(
      (stop) =>
        timeBandToInteger(stop.timeBand) <= timeBandToInteger(selectedTimeBand)
    );
  } else {
    stops = route.stops.filter(
      (stop) =>
        currentPostcode.getPostcode(zoomLevel) ===
          new Postcode(stop.postcode).getPostcode(zoomLevel) &&
        timeBandToInteger(stop.timeBand) <= timeBandToInteger(selectedTimeBand)
    );
  }

  return {
    stops: stops.length,
    parcels: getParcels({ stops }),
  };
};

export function numberWithCommas(x) {
  if (x === null) return "?";
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const getParcels = ({ stops }) =>
  stops?.reduce((acc, { stopParcels }) => (acc += stopParcels), 0) || 0;

export function getWeight(route) {
  if (!route || !route.stops) return 0;

  let grams = route.stops.reduce((acc, stop) => {
    return (acc += stop.stopWeight || 0);
  }, 0);

  return Math.floor(grams > 0 ? grams / 1000 : 0);
}

export const getRoutesPerTimeband = ({ routes, planType, collectionType }) => {
  const routesPerTimeband = {};
  each(routes, (route) => {
    if (!routeHasTerritory(route, planType, collectionType)) return;

    let isFinal = !route.plannedReadyTime;
    let timeband = isFinal
      ? DEFAULT_TIMEBAND
      : moment(route.plannedReadyTime).format("HH:mm");
    if (routesPerTimeband[timeband]) routesPerTimeband[timeband].push(route);
    else routesPerTimeband[timeband] = [route];
  });
  return routesPerTimeband;
};

export const routesAsObject = (routes, key = "routeCode") =>
  routes.reduce((routesObject, route) => {
    routesObject[route[key]] = route;
    return routesObject;
  }, {});

export const processRoutesProfile = async ({
  routeConfig,
  redirectConfig,
  routes,
  volumeData,
  overrideAreas,
  pickupAreas,
  locationConfig,
  lookupTables,
  planType,
}) => {
  const {
    routes: routesWithVolume,
    routesByOuter,
    cutOffTimes,
    routesTimes,
    overlayDetails,
  } = await processTimesAndVolumes({
    routeConfig,
    redirectConfig,
    routes,
    volumeData,
    overrideAreas,
    pickupAreas,
    locationConfig,
    lookupTables,
    planType,
  });

  let processedVolumeData = null;
  if (volumeData) {
    processedVolumeData = {
      overlayDetails: overlayDetailsAsObject(volumeData.overlayDetails),
      overlayProfile: { ...volumeData.overlayProfile },
    };
  }

  if (!!processedVolumeData) {
    processedVolumeData.overlayDetails = calculateTotals({
      overlayData: overlayDetails,
      planType,
      validPostcodes: Object.keys(lookupTables?.POSTCODE || {}),
    });
  }

  return {
    routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  };
};

export const generateRouteDaysConfig = ({ routes, routeDayLocks }) =>
  routes.map(({ routeCode }) => ({
    routeCode,
    checksum: routeDayLocks[routeCode].checksum,
    lockedByUserCode: routeDayLocks[routeCode].lockedByUserCode,
    lockedByUserName: routeDayLocks[routeCode].lockedByUserName,
    lockedDatetime: routeDayLocks[routeCode].lockedDatetime,
  }));

export const getNewRedirectionRoute = (
  planType = DEFAULT_PLAN_TYPE,
  timeBand
) => ({
  ...DEFAULT_ROUTE,
  routeCode: getRedirectionRouteCode(planType, timeBand),
  routeType: planType,
  plannedReadyTime: isHHMMFormat(timeBand)
    ? getTimestampFromHHMM(timeBand)
    : null,
  plannedReadyTimeAsString: timeBand !== DEFAULT_TIMEBAND ? timeBand : null,
});

export const getRedirectionRouteCode = (
  planType = DEFAULT_PLAN_TYPE,
  timeBand
) => {
  if (timeBand === DEFAULT_TIMEBAND) return `${planType}FW`;
  const [hours, minutes] = timeBand.split(":");
  const hoursId = hours * 4;
  const minutesId = !minutes ? 0 : minutes / 15;
  const paddedId = (hoursId + minutesId).toString().padStart(2, 0);
  return `${planType}${paddedId}`;
};

export const isRouteCode = (routeCode) =>
  /\d{3}|([BCDIORS](\d{2}|FW))$/.test(routeCode);

export const isRedirectionRouteCode = (routeCode) =>
  /[BCDIORS](\d{2}|FW)$/.test(routeCode);

export const isNonRedirectionRouteCode = (routeCode) =>
  /\d{3}$/.test(routeCode);

export const validateRoute = ({
  route,
  maxAdditionalMinutes,
  routeConfig,
  existingRoutes,
}) => {
  const errors = {};
  const { routeCode, additionalMinutes, roadSpeeds } = route;
  if (
    existingRoutes &&
    existingRoutes.find((route) => route.routeCode === routeCode)
  )
    errors["routeCode"] = `Route ${routeCode} already exists.`;
  else if (!isNonRedirectionRouteCode(routeCode))
    errors["routeCode"] = `Route code must consist of 3 digits.`;

  if (!isHHMMFormat(additionalMinutes))
    errors["additionalMinutes"] = INVALID_TIME_FORMAT;
  else if (additionalMinutes > maxAdditionalMinutes)
    errors[
      "additionalMinutes"
    ] = `Additional minutes are exceeding allowed maximum of ${maxAdditionalMinutes}`;

  roadSpeeds.forEach(({ roadCode, urban, rural }) => {
    if (isNaN(+urban)) errors[`${roadCode}-urban`] = "Must be a number.";
    else if (urban > routeConfig[roadCode].maxSpeed)
      errors[
        `${roadCode}-urban`
      ] = `Must be less than ${routeConfig[roadCode].maxSpeed}.`;
    else if (urban < "01")
      errors[`${roadCode}-urban`] = `Must be less at least 1.`;

    if (isNaN(+rural)) errors[`${roadCode}-rural`] = "Must be a number.";
    else if (rural > routeConfig[roadCode].maxSpeed)
      errors[
        `${roadCode}-rural`
      ] = `Must be less than ${routeConfig[roadCode].maxSpeed}.`;
    else if (rural < "01")
      errors[`${roadCode}-rural`] = `Must be less at least 1.`;
  });

  return errors;
};
